import { useCallback, useMemo, useRef, useEffect } from "react";
import { AxiosError } from "axios";

import { useGlobalError } from "../../ErrorModal";
import { client } from "../../../api/client";
import { invalidTokenResponseInterceptor } from "../../../api/interceptors";
import { useAuthenticatedUserContext } from "../UserProvider";

export const useTokenExpiry = () => {
  const { error, setError } = useGlobalError();
  const { user, clearUser } = useAuthenticatedUserContext();

  // We capture the UI display boolean as a ref so that the `handleTokenExpiry`
  // callback doesn't re-evaluate based on the error appearing.
  const isShowingError = useRef(!!error);
  isShowingError.current = !!error;
  const handleTokenExpiry = useCallback(
    (err: AxiosError) => {
      if (!isShowingError.current && err) {
        setError({
          title: "Sign in required",
          message: "Please sign in again to continue",
          handlerText: "Sign in",
          onClearError: clearUser,
        });
      }
    },
    [clearUser, setError],
  );

  // Handles creation and injection of the interceptor after a user signed in
  // when no user has signed in, ProtectedRouter should handle sign in redirect.
  const interceptorId: number | undefined = useMemo(
    () =>
      !!user
        ? client.interceptors.response.use(
            undefined,
            invalidTokenResponseInterceptor(handleTokenExpiry),
          )
        : undefined,
    [handleTokenExpiry, user],
  );

  // Only handles ejection of an old interceptor on component unmount OR if the interceptor has been superseded
  const previousInterceptorId = useRef(interceptorId);
  useEffect(() => {
    // Handle a change in interceptor, ejecting the old one
    // Injection is handled in the `useMemo` above.
    if (
      previousInterceptorId.current !== undefined &&
      interceptorId !== previousInterceptorId.current
    ) {
      client.interceptors.response.eject(previousInterceptorId.current);
      previousInterceptorId.current = interceptorId;
    }

    // Handle the ejection of the interceptor on unmount
    return () => {
      if (interceptorId !== undefined) {
        client.interceptors.response.eject(interceptorId);
      }
    };
  }, [interceptorId, previousInterceptorId]);
};
